import React, { useState } from 'react';
import { Typography } from '@/UIlibrary/Typography/Typography';
import InputField from '@/UIlibrary/InputField/InputField';
import Button from '@/UIlibrary/Button/Button';
import { useTranslations } from 'use-intl';
import { useApolloClient } from '@/providers/MultiApolloProvider';
import { SCHEMAS } from '@/apolloClient';
import {
	useInviteUserMutation,
	InviteUserMutation,
	InviteUserInput
} from '@/graphql/__generated__/authentication';
import { toast } from 'react-hot-toast';
import logger from '@/logger';

const InviteMembers: React.FC = () => {
	const t = useTranslations('InviteMembers');
	const [email, setEmail] = useState('');
	const [fieldErrors, setFieldErrors] = useState<{ [key: string]: string }>({});
	const authenticationClient = useApolloClient(SCHEMAS.AUTHENTICATION);

	const [inviteUser, { loading }] = useInviteUserMutation({
		client: authenticationClient,
		onCompleted: (data: InviteUserMutation) => {
			if (!data.inviteUser) return;

			if (data.inviteUser.successful) {
				toast.success(t('InvitationSentSuccess'), { duration: 10000 });
				setEmail('');
				setFieldErrors({});
			} else {
				handleMutationErrors(data.inviteUser.mutationErrors);
			}
		},
		onError: (error) => {
			logger.error(error);
			toast.error(t('UnexpectedError'), { duration: 5000 });
		}
	});

	const handleMutationErrors = (
		errors: NonNullable<NonNullable<InviteUserMutation['inviteUser']>['mutationErrors']>
	) => {
		const newFieldErrors: { [key: string]: string } = {};

		errors.forEach((error) => {
			if (!error) return;

			switch (error.__typename) {
				case 'ActionFailed':
				case 'InvalidAction':
					toast.error(error.message || t('InvitationFailed'), { duration: 5000 });
					break;
				case 'InvalidInput': {
					const fieldName = error.field === 'invited_user_email' ? 'email' : error.field;
					newFieldErrors[fieldName] = error.message;
					break;
				}
			}
		});

		setFieldErrors(newFieldErrors);
	};

	const validateEmail = (emailValue: string): boolean => {
		const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
		if (!emailRegex.test(emailValue)) {
			return false;
		}
		const allowedDomains = ['fincluyente.com', 'kogni.studio'];
		const domain = emailValue.split('@')[1]?.toLowerCase();
		return allowedDomains.includes(domain);
	};

	const handleSubmit = async (event: React.FormEvent<HTMLFormElement>) => {
		event.preventDefault();
		setFieldErrors({});

		if (!email.trim()) {
			setFieldErrors({ email: t('EmailRequired') });
			return;
		}

		if (!validateEmail(email)) {
			const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
			if (!emailRegex.test(email)) {
				setFieldErrors({ email: t('InvalidEmailFormat') });
			} else {
				setFieldErrors({ email: t('InvalidEmailDomain') });
			}
			return;
		}

		try {
			const inviteUserInput: InviteUserInput = {
				invitedUserEmail: email.trim()
			};
			await inviteUser({ variables: { input: inviteUserInput } });
		} catch (err) {
			logger.error(err);
			toast.error(t('UnexpectedError'), { duration: 5000 });
		}
	};

	const isValidEmail = (emailValue: string): boolean => {
		if (!emailValue.trim()) return false;
		return validateEmail(emailValue);
	};

	const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
		const { value } = e.target;
		setEmail(value);
		if (fieldErrors.email) {
			setFieldErrors((prev) => {
				const updated = { ...prev };
				delete updated.email;
				return updated;
			});
		}
	};

	return (
		<div className="py-4 flex flex-col gap-6">
			<div className="flex w-full flex-col gap-5">
				<div className="flex flex-col gap-1">
					<Typography weight="semibold" size="lg">
						{t('Title')}
					</Typography>
					<Typography color="subtitle" size="sm">
						{t('Description')}
					</Typography>
				</div>
			</div>
			<hr />
			<form onSubmit={handleSubmit} className="flex flex-col gap-4 w-full md:w-1/2">
				<div className="flex flex-row gap-2 items-start">
					<div className="flex-1">
						<InputField
							name="email"
							type="email"
							label={t('EmailLabel')}
							value={email}
							onChange={handleChange}
							loading={loading}
							disabled={loading}
							placeholder={t('EmailPlaceholder')}
							size="auto"
							error={!!fieldErrors.email}
							hint={fieldErrors.email || t('EmailHint')}
							required
						/>
					</div>
					<div className="pt-[28px]">
						<Button
							type="submit"
							disabled={loading || !isValidEmail(email)}
							loading={loading}
							label={t('SendInvitation')}
							size="auto"
						/>
					</div>
				</div>
				<div className="flex flex-col gap-2">
					<Typography color="subtitle" size="sm">
						{t('InvitationProcessInfo')}
					</Typography>
					<Typography color="subtitle" size="sm" weight="semibold">
						{t('EmailDomainDisclaimer')}
					</Typography>
				</div>
			</form>
		</div>
	);
};

export default InviteMembers;
